import uniq from 'lodash/uniq'
import { v4 } from 'uuid'

import { AppError } from '@app/errors/AppError'
import { displayMessage } from '@app/errors/displayMessage'

import { createSelector } from '@app/utils/createSelector'

import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'

import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserIdSelector } from '@app/store/selectors/profile'
import { createThunk } from '@app/store/thunk'

import { usersModelsSelector as usersSelector } from './users'

const favoritesListSlice = createReduxSlice<string[]>('favorites_list')

const favoritesMetaSlice = createReduxSlice<FavoritesMeta>('favorites_meta')

const favoritesSyncSlice = createReduxSlice<{ hash: string }>('favorites_sync')

/**
 * What the loaded list is keyed on: whose favorites these are, and the last invalidation.
 * Up here with the slices because the selectors below take it as an argument.
 */
const favoritesHashSelector = createSelector([profileUserIdSelector, favoritesSyncSlice.selector], (id, sync) => (id ? [id, sync?.hash].join(':') : null))

/** Invalidates the loaded list: the next read of the selector fetches page one afresh. */
export const invalidateFavoritesSlice = () =>
  createThunk(dispatch => {
    // The error goes with it: it belongs to the read being replaced, and would otherwise sit over
    // the list until the new one lands.
    dispatch(favoritesMetaSlice.update(state => (state?.error ? { ...state, error: null } : state)))
    dispatch(favoritesSyncSlice.set({ hash: v4() }))
  })

export const addFavoriteSlice = (id: string) => favoritesListSlice.update(state => uniq([id, ...(state ?? EMPTY_LIST)]))

export const removeFavoriteSlice = (id: string) => favoritesListSlice.update(state => (state ?? EMPTY_LIST).filter(listId => listId !== id))

/** The next page of the list, for the endless scroll; the first page is the selector's own job. */
export const loadNextFavorites = () =>
  createThunk(async (dispatch, getState) => {
    const meta = favoritesMetaSelector(getState())
    await dispatch(readFavoritesPage((meta.page || 0) + 1, meta.per_page || FAVORITES_PER_PAGE))
  })

/** Every page at once — what the chat sidebar needs, matching favorites against its channels. */
export const loadAllFavorites = (per_page: number = 50) =>
  createThunk(async (dispatch, getState) => {
    let page = 1
    while (true) {
      await dispatch(readFavoritesPage(page, per_page))
      const meta = favoritesMetaSelector(getState())
      if (meta.page >= meta.total_pages) return
      page = meta.page + 1
    }
  })

export const favoritesMetaSelector = createSelector([favoritesMetaSlice.selector], meta => meta ?? DEFAULT_META)

export const favoritesListSelector = createSelector([favoritesListSlice.selector], list => list ?? EMPTY_LIST)

/**
 * Favorite users' chat_server identities — the external_ids chat_server channels
 * carry. `chat_server_uid` (UUID), NOT the legacy `chat_uid` (a Rails id).
 */
export const favoritesChatUidsListSelector = createSelector(
  [favoritesListSelector, usersSelector],
  (list, users) => {
    return list.map(id => users[id]?.chat_server_uid ?? null).filter((chatUid): chatUid is string => !!chatUid)
  },
  { resultEqual: (a, b) => a.join(',') === b.join(',') }
)

export const createIsUserFavoriteSelector = (userIdSelector: (...args: any[]) => string | null) =>
  createSelector([userIdSelector, favoritesListSelector], (id, list) => (id ? list.includes(id) : false))

/**
 * The first page, loaded on demand. Later pages stay imperative (`loadNextFavorites` through
 * `Endless`) and merge into the same list — a paged list is not what a thunk selector models,
 * only its first read is.
 */
export const favoritesIdsResultSelector = createSelector([favoritesListSelector, favoritesMetaSelector, favoritesHashSelector], (list, meta, hash) =>
  createLoadable(c => {
    if (hash === null) return EMPTY_LIST
    if (meta.hash !== hash) {
      return c.load(
        createPromise('Favorites must be fetched', hash, async dispatch => {
          await dispatch(loadFirstFavoritesPage(hash))
        })
      )
    }
    return list
  })
)

export const favoritesUsersResultSelector = createSelector([favoritesIdsResultSelector, usersSelector], (idsResult, users) =>
  createLoadable(c =>
    c.unwrap(idsResult).map(id => {
      const user = users[id]
      if (!user) throw new AppError(`Favorite user ${id} is missing`).withExpose(true)
      return user
    })
  )
)

/**
 * The load the selector waits on, which does not fail. A refresh that could not be made is stamped
 * with the hash it was asked for anyway, so the selector resolves to whatever was already loaded
 * rather than blanking the page, and the failure rides in the meta for the list to show above it.
 */
const loadFirstFavoritesPage = (hash: string) =>
  createThunk(async (dispatch, getState) => {
    try {
      await dispatch(readFavoritesPage(1, FAVORITES_PER_PAGE))
    } catch (error) {
      // Nothing loaded before means nothing to fall back on: the failure is the whole answer, and
      // it goes to the boundary rather than into a slice that would read as an empty list.
      if (!favoritesMetaSelector(getState()).fetched) throw error
      dispatch(favoritesMetaSlice.update(state => ({ ...(state ?? DEFAULT_META), hash, error: displayMessage(error), loading: false })))
    }
  })

/** One page, with the list's loading flag around it and the localized failure callers expect. */
const readFavoritesPage = (page: number, per_page: number) =>
  createThunk(async dispatch => {
    const [{ getFavorites }, { callV2Api }, { catchFetchDataError }] = await Promise.all([
      import('@app/packages/api/v2/actions/favorites'),
      import('@app/store/actions/api'),
      import('@app/utils/performFetchData'),
    ])
    dispatch(favoritesMetaSlice.update(state => ({ ...(state ?? DEFAULT_META), loading: true })))
    try {
      const resp = await dispatch(callV2Api(getFavorites(page, per_page))).catch(catchFetchDataError(dispatch, 'Favorites fetch failed'))
      dispatch(addFavoritesPageSlice({ page, per_page }, resp))
      return resp
    } catch (e) {
      dispatch(favoritesMetaSlice.update(state => ({ ...(state ?? DEFAULT_META), loading: false })))
      throw e
    }
  })

/**
 * Uniq-merge every page: /favorites and the chat sidebar both page through favorites, and a
 * page-1 reset from one would drop what the other accumulated. Removals are explicit, so
 * merging never resurrects an unfavorited user.
 */
const addFavoritesPageSlice = (query: { page: number; per_page: number }, resp: { data: { id: string }[]; meta: FavoritesPageMeta }) =>
  createThunk((dispatch, getState) => {
    // Stamped with the hash the page was read under, which is what the selector compares against.
    // Whoever fetched it — this route or the chat sidebar — the other reads the result as fresh.
    const hash = favoritesHashSelector(getState())
    dispatch(favoritesListSlice.update(state => uniq((state ?? EMPTY_LIST).concat(resp.data.map(model => model.id)))))
    dispatch(
      favoritesMetaSlice.update(state => ({
        ...(state ?? DEFAULT_META),
        ...query,
        hash,
        error: null,
        page: resp.meta.current_page,
        total: resp.meta.total,
        total_pages: resp.meta.total_pages,
        fetched: true,
        loading: false,
      }))
    )
  })

type FavoritesPageMeta = { total: number; total_pages: number; current_page: number }

type FavoritesMeta = {
  /** The hash the loaded pages were read under; null until the first page lands. */
  hash: string | null
  /** Why the last read failed, for the page to show over whatever is still on screen. */
  error: string | null
  fetched: boolean
  loading: boolean
  page: number
  per_page: number
  total: number
  total_pages: number
}

const EMPTY_LIST: string[] = []

const FAVORITES_PER_PAGE = 16

const DEFAULT_META: FavoritesMeta = {
  hash: null,
  error: null,
  fetched: false,
  loading: false,
  page: 0,
  per_page: 0,
  total: 0,
  total_pages: 0,
}
